var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import DeleteDocumentAlertConfigModal from '@web/components/DocAlerts/DocAlertsConfigTab/DeleteDocumentAlertConfigModal';
import Table from '@web/components/layout/Table/Table';
import Toggle from '@web/components/layout/Toggle/Toggle';
import { useUserContext } from '@web/contexts/User';
import { useToast } from '@web/hocs/useToast';
import { trpc } from '@web/lib/trpc';
import { getIconByType } from '@web/utility';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
var DocAlertsConfigSection = function (_a) {
    var documentAlertConfigData = _a.documentAlertConfigData, refetch = _a.refetch, setDocumentAlertConfig = _a.setDocumentAlertConfig;
    var t = useTranslation().t;
    var toast = useToast().toast;
    var updateDocumentAlertConfig = trpc.updateDocumentAlertConfig.useMutation();
    var currentUser = useUserContext().currentUser;
    var _b = useState(null), deleteAlertConfigId = _b[0], setDeleteAlertConfigId = _b[1];
    var isAdmin = Boolean(currentUser === null || currentUser === void 0 ? void 0 : currentUser.isAdmin);
    var columns = __spreadArray([
        {
            title: t('docAlerts.docAlertsConfig.docAlertsConfigTable.documents'),
            isVisible: true,
            className: 'w-[28%] sm:px-3'
        },
        {
            title: t('docAlerts.docAlertsConfig.docAlertsConfigTable.trigger'),
            isVisible: true,
            className: 'sm:px-3'
        }
    ], (isAdmin
        ? [
            {
                title: t('docAlerts.docAlertsConfig.docAlertsConfigTable.actions'),
                isVisible: true,
                className: 'w-36 sm:px-3 whitespace-nowrap'
            }
        ]
        : []), true);
    var columnColStyles = isAdmin
        ? [{ width: '28%' }, {}, { width: '9rem' }]
        : [{ width: '35%' }, {}];
    var handleToggleChange = function (documentAlertConfig) {
        updateDocumentAlertConfig.mutate({
            documentAlertConfigId: documentAlertConfig.id,
            isActive: !documentAlertConfig.isActive
        }, {
            onSuccess: function () {
                refetch();
                toast({
                    title: t('docAlerts.docAlertsConfig.statusUpdated'),
                    description: t('generalSettings.settingsSaved'),
                    variant: 'success',
                    duration: 3000
                });
            }
        });
    };
    if (documentAlertConfigData === null)
        return null;
    var tableData = documentAlertConfigData.map(function (alertConfig) {
        var enabledTriggers = [
            {
                key: 'shareExternally',
                isActive: alertConfig.isShareExternally
            },
            {
                key: 'shareViaLink',
                isActive: alertConfig.isShareViaLink
            },
            {
                key: 'shareInternally',
                isActive: alertConfig.isShareAnyone
            },
            {
                key: 'requestAccess',
                isActive: alertConfig.isRequestAccess
            },
            {
                key: 'download',
                isActive: alertConfig.isDownload
            },
            {
                key: 'print',
                isActive: alertConfig.isPrint
            },
            {
                key: 'sendToEmail',
                isActive: alertConfig.isSendToEmail
            },
            {
                key: 'publish',
                isActive: alertConfig.isPublish
            },
            {
                key: 'copy',
                isActive: alertConfig.isCopy
            },
            {
                key: 'edited',
                isActive: alertConfig.isEdited
            },
            {
                key: 'deleteTrash',
                isActive: alertConfig.isDeleteTrash
            },
            {
                key: 'renameMove',
                isActive: alertConfig.isRenameMove
            }
        ]
            .filter(function (trigger) { return trigger.isActive; })
            .map(function (trigger) {
            return t("docAlerts.docAlertsConfig.docAlertsConfigTable.triggers.".concat(trigger.key));
        })
            .join(', ');
        return __spreadArray([
            {
                id: "".concat(alertConfig.id, "-title"),
                render: function () {
                    return alertConfig.documentAlertConfigList.map(function (docAlert, index) { return (_jsxs("div", __assign({ className: "flex min-w-0 items-center gap-1" }, { children: [_jsx("span", __assign({ className: "shrink-0" }, { children: getIconByType(docAlert.document.type, {
                                    width: '16',
                                    height: '16'
                                }) })), _jsx("span", __assign({ className: "truncate" }, { children: docAlert.document.title }))] }), index)); });
                }
            },
            {
                id: "".concat(alertConfig.id, "-trigger"),
                render: function () { return (_jsx("p", __assign({ className: "text-xs text-gray-500 font-normal" }, { children: enabledTriggers }))); }
            }
        ], (isAdmin
            ? [
                {
                    id: "".concat(alertConfig.id, "-actions"),
                    className: 'align-middle whitespace-nowrap',
                    render: function () { return (_jsxs("div", __assign({ className: "flex items-center gap-3" }, { children: [_jsx(Toggle, { onChange: function () { return handleToggleChange(alertConfig); }, enabled: alertConfig.isActive }), _jsx(PencilIcon, { className: "h-5 w-5 text-indigo-600 cursor-pointer hover:text-indigo-800 hover:scale-105", onClick: function () {
                                    setDocumentAlertConfig(alertConfig);
                                } }), _jsx(TrashIcon, { className: "h-5 w-5 text-indigo-600 cursor-pointer hover:text-indigo-800 hover:scale-105", onClick: function () { return setDeleteAlertConfigId(alertConfig.id); } })] }))); }
                }
            ]
            : []), true);
    });
    return (_jsxs("div", __assign({ className: "mt-5" }, { children: [_jsx(Table, { columns: columns, data: tableData, striped: true, columnColStyles: columnColStyles }), _jsx(DeleteDocumentAlertConfigModal, { visible: deleteAlertConfigId !== null, documentAlertConfigId: deleteAlertConfigId, onClose: function () { return setDeleteAlertConfigId(null); }, onDeleted: refetch })] })));
};
export default DocAlertsConfigSection;
